/**
 * Settings (ui/DESIGN.md: kept, restyled, minimal), behind the avatar: the vaults with their install tokens, the
 * data model as discovered from the data with the data models of your own, the users of the tenant, and the
 * tenants with their administrators (platform admins). Everything happens inline on the page: a token, a new
 * user or a question before deleting is a panel that opens under the title, never a dialog. The password is
 * changed from the avatar menu (shell/Shell.tsx).
 */
import { keyCountLabel } from '../lib/connectors.ts';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type Environment, type VaultInfo, type Role, type User } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { useAsync } from '../lib/hooks.ts';
import { ago, bytes, count, dateTime, duration, quote } from '../lib/format.ts';
import { highlightParts } from '../lib/highlight.ts';
import { KIND_FILTERS, kindCounts, matchesKind, percent, KIND_LABEL, statOf, type FieldStat, type KindFilter } from '../lib/fieldstats.ts';
import { CLASS_STATUS_FILTERS, CLEAR_FILTERS, FIELD_PAGE, facetsOf, isFiltered, patchView, toggleFacet, readTab, readView, withTab, type ClassStatusFilter, type FieldSort, type ModelView, type SettingsTab } from '../lib/modelview.ts';
import { useCatalog, useNewCount, useNewElsewhere } from '../lib/catalogs.tsx';
import { searchWithEnv } from '../lib/env.ts';
import { useNewSystemsCount } from '../lib/systemsdata.tsx';
import { GROUP_BYS, GROUP_LABEL, NO_GROUP, SENSITIVITY_LABEL, STATUS_LABEL, classFacets, domainOf, groupClasses, hasSuggestions, listClasses, newClasses, sensitivityOf, statusCounts, statusOf, suggestionTitle, type CatalogEntry, type CatalogStatus, type Facet, type FacetValue, type Sensitivity } from '../lib/catalog.ts';
import { CatalogEditor } from '../components/CatalogEditor.tsx';
import { CatalogValue } from '../components/Suggested.tsx';
import { systemCode, systemName } from '../lib/systems.ts';
import { FacetChip, FacetRow, Segmented } from '../components/FilterChips.tsx';
import { DistinctValues } from '../components/DistinctValues.tsx';
import { VALUE_PAGE } from '../lib/distinct.ts';
import { useFormDraft, useSyncedText, useUrlFlag } from '../lib/formstate.ts';
import { clearForm } from '../lib/pagedraft.ts';
import { loadLine } from '../lib/vault.ts';
import { certificateWarning } from '../lib/certificate.ts';
import { Shell } from '../shell/Shell.tsx';
import { IconButton, Kicker, Menu, Surface, SystemBadge, cx, noun } from '../design/primitives.tsx';
import { ArrowIcon, BackIcon, SearchIcon, AlertIcon, BuildingIcon, CheckIcon, ChevronIcon, DatabaseIcon, LayersIcon, CloseIcon, CopyIcon, FilterIcon, KeyIcon, PencilIcon, PlusIcon, SchemaIcon, ServerIcon, TrashIcon, UsersIcon } from '../components/icons.tsx';
import { EnvDot, EnvLabel, EnvPicker, EnvSelect } from '../components/EnvBits.tsx';
import { SearchHint } from '../components/SearchHint.tsx';
import { Button, Field, Spinner, inputClass, useToast } from '../components/ui.tsx';
import { VirtualModels } from './Models.tsx';
import { AddToModel } from '../components/AddToModel.tsx';
import { ALL, RebuildModel } from '../components/RebuildModel.tsx';
import { ADD_KEY, readAdd, withAdd } from '../lib/modeledit.ts';
import { useModalSteps, useUrlModal } from '../lib/urlmodal.ts';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

export function SettingsLayout() {
  const { canManage, isAdmin } = useAuth();
  const { link } = useEnv();
  const fresh = useNewCount();
  const freshSystems = useNewSystemsCount();
  const elsewhere = useNewElsewhere();
  // The number of new things to check beside an item: data models, systems
  const badges: Record<string, [number, string]> = { '/settings/model': [fresh, 'data model'], '/settings/systems': [freshSystems, 'system'] };
  const links: [string, string, typeof ServerIcon][] = [
    ['/settings/vaults', 'Vaults', ServerIcon], ['/settings/environments', 'Environments', LayersIcon], ['/settings/model', 'Data model', SchemaIcon],
    ['/settings/systems', 'Systems', DatabaseIcon],
    ...(canManage ? [['/settings/connectors', 'Connectors', KeyIcon] as [string, string, typeof ServerIcon]] : []),
    ...(canManage ? [['/settings/users', 'Users', UsersIcon] as [string, string, typeof ServerIcon]] : []),
    ...(isAdmin ? [['/settings/tenants', 'Tenants', BuildingIcon] as [string, string, typeof ServerIcon]] : []),
  ];
  return (
    <Shell>
      <div className="w-full px-6 pt-6 pb-20 flex gap-10 flex-wrap lg:flex-nowrap">
        <nav aria-label="Settings" className="flex lg:flex-col gap-1 w-full lg:w-52 shrink-0 self-start lg:sticky lg:top-[calc(var(--shell-h)+24px)]">
          <Kicker className="hidden lg:block px-3 pb-2">Settings</Kicker>
          {links.map(([to, label, Icon]) => (
            <div key={to} className="flex flex-col">
              <NavLink to={link(to)} className={({ isActive }) => cx('flex items-center gap-2.5 h-9 px-3 rounded-chip text-small no-underline transition-colors duration-[var(--t-micro)] ease-mv',
                isActive ? 'bg-n2 text-n10 font-medium shadow-node' : 'text-n8 hover:text-n10 hover:bg-n2/60')}>
                <Icon size={16} />{label}
                {(badges[to]?.[0] ?? 0) > 0 && (
                  <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-accent-soft text-accent text-micro font-semibold tabular-nums grid place-items-center"
                    title={`${count(badges[to]![0])} new ${badges[to]![1]}${badges[to]![0] === 1 ? '' : 's'} to check`} aria-label={`${count(badges[to]![0])} new to check`}>{count(badges[to]![0])}</span>
                )}
              </NavLink>
              {to === '/settings/model' && elsewhere.length > 0 && <NewElsewhere items={elsewhere} className="pl-[38px] pr-2 pt-0.5 pb-1" />}
            </div>
          ))}
        </nav>
        <div className="flex-1 min-w-0"><Outlet /></div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------- the pieces the pages share

/**
 * "+1 in Production · +3 in QA": new data models of other environments that this one does not have. Each opens Settings →
 * Data model in that environment (a normal navigation: Back returns), with only the new ones listed.
 */
export function NewElsewhere({ items, className, prefix }: { items: { env: Environment; count: number }[]; className?: string; prefix?: string }) {
  return (
    <span className={cx('flex items-center gap-x-1.5 gap-y-0.5 flex-wrap text-micro text-n7', className)} aria-label="New data models in other environments">
      {prefix}
      {items.map(({ env, count: n }, i) => (
        <span key={env.id} className="inline-flex items-center gap-1.5">
          {i > 0 && <span aria-hidden="true">·</span>}
          <Link to={`/settings/model${searchWithEnv('status=new', env.id)}`} title={`${count(n)} new data model${n === 1 ? '' : 's'} only in ${env.name}: switch to it`}
            className="inline-flex items-center gap-1 text-n8 no-underline hover:text-accent hover:underline">
            <EnvDot color={env.color} />+{count(n)} in {env.name}
          </Link>
        </span>
      ))}
    </span>
  );
}

export function Title({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
      <h1 className="m-0 text-title font-semibold tracking-tight">{children}</h1>
      {action}
    </div>
  );
}

/** A form that opens under the title and closes when done. */
export function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <Surface className="p-5 flex flex-col gap-4 mv-pop">
      <div className="flex items-center justify-between gap-3">
        <span className="text-body font-semibold">{title}</span>
        <IconButton label="Close" onClick={onClose}><CloseIcon size={16} /></IconButton>
      </div>
      {children}
    </Surface>
  );
}

/** The question before something is taken away, in place of a dialog. */
export function Confirm({ question, action, onConfirm, onCancel, children }: {
  question: string; action: string; onConfirm: () => void; onCancel: () => void; children: ReactNode;
}) {
  return (
    <Surface role="group" aria-label={question} className="p-4 flex items-center gap-4 flex-wrap mv-pop">
      <div className="flex-1 min-w-[240px] flex flex-col gap-0.5">
        <span className="text-body font-semibold">{question}</span>
        <span className="text-small text-n8">{children}</span>
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm}>{action}</Button>
      </div>
    </Surface>
  );
}

export const th = 'text-micro font-medium uppercase tracking-[.08em] text-n7 text-left px-4 py-3 whitespace-nowrap';
export const td = 'px-4 py-3 border-t border-n4 text-small align-top';
const row = 'cursor-pointer transition-colors duration-[var(--t-micro)] hover:bg-n3/60';

// ---------------------------------------------------------------- vaults

export function VaultsSettings() {
  const { tenant, canManage } = useAuth();
  const toast = useToast();
  const tid = tenant?.id ?? '';
  const { envs, current, link } = useEnv();
  const navigate = useNavigate();
  // Owners see the vaults of every environment here, labelled by environment; others those of the one they look at
  const vaults = useAsync(() => Api.vaults(tid, canManage ? 'all' : undefined), [tid, canManage], 10_000);
  // Open panels are in the URL (replace), so Back returns to the page with the panel as it was
  const [install, setInstall] = useUrlFlag('install');
  const [revoking, setRevoking] = useState<VaultInfo>();
  const [moving, setMoving] = useState<{ vault: VaultInfo; to: Environment }>();
  const envOf = (id: string) => envs.find((e) => e.id === id);
  const order = (n: VaultInfo) => envOf(n.environmentId)?.position ?? 0;
  const active = (vaults.data ?? []).filter((n) => !n.revokedAt).sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
  const move = async (vault: VaultInfo, to: Environment) => {
    try {
      await Api.moveVault(tid, vault.id, to.id);
      toast(`Vault ${vault.name} moved to ${to.name}`);
      vaults.reload();
    } catch (e) {
      toast(message(e), 'fail');
    }
    setMoving(undefined);
  };
  const askMove = (vault: VaultInfo, toId: string) => {
    const to = envOf(toId);
    if (!to || to.id === vault.environmentId) return;
    if (to.protected || envOf(vault.environmentId)?.protected) setMoving({ vault, to });
    else void move(vault, to);
  };
  const revoked = (vaults.data ?? []).filter((n) => n.revokedAt);

  return (
    <>
      <Title action={canManage && <Button variant="primary" aria-expanded={install} onClick={() => { if (install) clearForm(`install.${tid}`); setInstall(!install); }}><PlusIcon size={16} />Install a vault</Button>}>Vaults</Title>
      <div className="flex flex-col gap-4">
        {install && tenant && <InstallPanel tenantId={tenant.id} envs={envs} initialEnv={current?.id ?? ''} onClose={() => setInstall(false)} />}
        {revoking && (
          <Confirm question={`Revoke vault ${revoking.name}?`} action="Revoke" onCancel={() => setRevoking(undefined)} onConfirm={async () => {
            try {
              await Api.revokeVault(tid, revoking.id);
              toast(`Vault ${revoking.name} revoked`);
              vaults.reload();
            } catch (e) {
              toast(message(e), 'fail');
            }
            setRevoking(undefined);
          }}>
            The vault is disconnected at once and can no longer connect. Its data stays on the customer's server; to use it again it must be registered with a new install token.
          </Confirm>
        )}
        {moving && (
          <Confirm question={`Move vault ${moving.vault.name} to ${moving.to.name}?`} action="Move" onCancel={() => setMoving(undefined)} onConfirm={() => void move(moving.vault, moving.to)}>
            {envOf(moving.vault.environmentId)?.protected && moving.to.protected
              ? `Both ${envOf(moving.vault.environmentId)?.name} and ${moving.to.name} are protected. `
              : moving.to.protected ? `${moving.to.name} is protected. ` : `${envOf(moving.vault.environmentId)?.name} is protected. `}
            The vault's data and its connector senders follow it: it disappears from {envOf(moving.vault.environmentId)?.name ?? 'its environment'} and appears in {moving.to.name} at once.
          </Confirm>
        )}
        {vaults.error && <p className="m-0 text-small text-fail">{vaults.error}</p>}
        {vaults.loading && !vaults.data && <Spinner />}
        {vaults.data && active.length === 0 && <Surface className="p-10 text-center text-small text-n8">No vaults yet. Install one on a server in the customer's network.</Surface>}
        {active.length > 0 && (
          <Surface className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse">
              <thead>
                <tr>
                  <th className={th}>Vault</th><th className={th}>Environment</th><th className={th}>Connection</th><th className={th}>Version</th>
                  <th className={cx(th, 'text-right')}>Documents</th><th className={cx(th, 'text-right')}>Disk</th>
                  <th className={th}>Data</th><th className={th}>Load</th><th className={th} />
                </tr>
              </thead>
              <tbody>
                {active.map((n) => {
                  const days = n.status?.partitions?.map((p) => p.day).sort() ?? [];
                  return (
                    <tr key={n.id} className={row} onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,select,input')) void navigate(link(`/settings/vaults/${n.id}`)); }}>
                      <td className={td}>
                        <Link to={link(`/settings/vaults/${n.id}`)} className="font-medium text-n10 no-underline hover:text-accent focus-visible:ring-[1.5px] focus-visible:ring-accent rounded-chip">{n.name}</Link>
                        <div className="text-micro text-n7 mt-0.5">{n.host?.hostname ?? n.hostname ?? n.reportedName}{keyCountLabel(n.connectorKeyCount) && <span> · {keyCountLabel(n.connectorKeyCount)}</span>}</div>
                        <CertificateChip tls={n.tls} />
                        {(n.config?.pendingRestart.length ?? 0) > 0 && (
                          <div className="mt-1 inline-flex items-center gap-1.5 text-micro text-n8"><i className="size-1.5 rounded-full bg-warn" aria-hidden="true" />restart needed</div>
                        )}
                      </td>
                      <td className={td}>
                        {canManage && envs.length > 1 ? (
                          <span className="inline-flex items-center gap-2">
                            <EnvDot color={envOf(n.environmentId)?.color} />
                            <select aria-label={`Environment of ${n.name}`} value={n.environmentId} onChange={(e) => askMove(n, e.target.value)}
                              className="h-8 rounded-chip bg-transparent hover:bg-n3 text-small text-n10 border-0 outline-none cursor-pointer pr-1 focus-visible:ring-[1.5px] focus-visible:ring-accent">
                              {envs.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                          </span>
                        ) : <EnvLabel env={envOf(n.environmentId)} className="text-n10" />}
                      </td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-2 text-n10">
                          <span className={cx('size-2 rounded-full shrink-0', n.online ? 'bg-ok' : 'bg-n6')} aria-hidden="true" />{n.online ? 'Online' : 'Offline'}
                        </span>
                        <div className="text-micro text-n7 mt-0.5 whitespace-nowrap">seen {ago(n.lastSeen)}</div>
                      </td>
                      <td className={cx(td, 'text-n9 whitespace-nowrap')}>{n.version ?? '—'}</td>
                      <td className={cx(td, 'text-right font-mono tabular-nums text-n9 whitespace-nowrap')}>{count(n.status?.messages)}</td>
                      <td className={cx(td, 'text-right font-mono tabular-nums text-n9 whitespace-nowrap')}>{bytes(n.status?.bytesOnDisk)}</td>
                      <td className={cx(td, 'text-n9 whitespace-nowrap')}>
                        {days.length ? <>{days[0]} – {days[days.length - 1]}<div className="text-micro text-n7 mt-0.5">{days.length} days</div></> : '—'}
                      </td>
                      <td className={td}><LoadCell host={n.host} /></td>
                      <td className={cx(td, 'text-right py-2')}>
                        {canManage && <IconButton label={`Revoke ${n.name}`} onClick={() => setRevoking(n)}><TrashIcon size={16} /></IconButton>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Surface>
        )}
        {revoked.length > 0 && <p className="m-0 text-small text-n7">Revoked: {revoked.map((n) => `${n.name} (${dateTime(n.revokedAt)})`).join(', ')}</p>}
      </div>
    </>
  );
}

/** A quiet chip when the certificate expires within 14 days, has expired, or its managed renewal failed: icon and words, not colour alone. */
function CertificateChip({ tls }: { tls: VaultInfo['tls'] }) {
  const w = certificateWarning(tls);
  if (!w) return null;
  return (
    <div className={cx('mt-1 inline-flex items-center gap-1.5 rounded-full bg-n3 h-5 px-2 text-micro whitespace-nowrap', w.tone === 'fail' ? 'text-fail' : 'text-warn')}>
      <AlertIcon size={12} className="shrink-0" />{w.text}
    </div>
  );
}

/** "disk 62% used" for a row; a high load carries a mark as well as a colour. */
function LoadCell({ host }: { host: VaultInfo['host'] }) {
  const l = loadLine(host);
  if (!l) return <span className="text-n7" title="This vault does not report resource data yet">—</span>;
  return (
    <span className={cx('inline-flex items-start gap-1.5', l.tone === 'ok' ? 'text-n9' : l.tone === 'warn' ? 'text-warn' : 'text-fail')}>
      {l.tone !== 'ok' && <AlertIcon size={14} className="mt-0.5 shrink-0" />}
      <span className="flex flex-col">{l.text.split(' · ').map((t) => <span key={t} className="whitespace-nowrap">{t}</span>)}</span>
    </span>
  );
}

/** A one-time install token and the three commands that register a vault with it, on the page. */
function InstallPanel({ tenantId, envs, initialEnv, onClose }: { tenantId: string; envs: Environment[]; initialEnv: string; onClose: () => void }) {
  const [{ hours, envId }, setDraft, clearDraft] = useFormDraft(`install.${tenantId}`, { hours: '24', envId: initialEnv });
  const setHours = (v: string) => setDraft({ hours: v });
  const setEnvId = (v: string) => setDraft({ envId: v });
  const close = () => { clearDraft(); onClose(); };
  const target = envs.find((e) => e.id === envId);
  const [token, setToken] = useState<{ token: string; expiresAt: number }>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      setToken(await Api.createInstallToken(tenantId, Number(hours) || 24, envId || undefined));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  const register = token && `java -jar messagevault-vault.jar register --cloud ${window.location.origin} --token ${token.token} vault.json`;
  return (
    <Panel title="Install a vault" onClose={close}>
      {!token ? (
        <form onSubmit={(e) => void create(e)} className="flex flex-col gap-4">
          <p className="m-0 text-small text-n8 max-w-2xl">
            Create a one-time install token. The vault registers with it, gets its own key pair and then connects to this service on its own: no inbound firewall openings at the customer.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            {envs.length > 1 && (
              <Field label="Environment" className="w-56">
                <EnvSelect envs={envs} value={envId} onChange={setEnvId} />
              </Field>
            )}
            <Field label="Token valid for (hours)" className="w-44">
              <input className={inputClass} type="number" min={1} max={720} value={hours} onChange={(e) => setHours(e.target.value)} />
            </Field>
            <Button type="submit" variant="primary" disabled={busy}>Create token</Button>
          </div>
          {error && <p className="m-0 text-small text-fail">{error}</p>}
        </form>
      ) : (
        <div className="flex flex-col gap-3 text-small">
          <p className="m-0 flex items-center gap-2 text-n9">
            <span className="size-2 rounded-full bg-warn shrink-0" aria-hidden="true" />
            The token is shown only once{target && envs.length > 1 ? `; the vault it registers joins ${target.name}` : ''}. Valid until {dateTime(token.expiresAt)}, single use.
          </p>
          <Step n={1}>Create vault.json on the server (data directory, users for senders). Hash a password with:</Step>
          <Copyable text="java -jar messagevault-vault.jar hash-password" />
          <Step n={2}>Register the vault:</Step>
          <Copyable text={register!} />
          <Step n={3}>Start it. It connects here and appears in the list:</Step>
          <Copyable text="java -jar messagevault-vault.jar serve vault.json" />
          <div className="flex justify-end"><Button variant="primary" onClick={close}>Done</Button></div>
        </div>
      )}
    </Panel>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <p className="m-0 flex gap-2.5 items-start text-n9">
      <span className="size-5 rounded-full bg-accent-soft text-accent text-micro font-semibold grid place-items-center shrink-0 mt-px">{n}</span>
      <span>{children}</span>
    </p>
  );
}

export function Copyable({ text }: { text: string }) {
  const toast = useToast();
  return (
    <div className="flex gap-2 items-start">
      <code className="flex-1 font-mono text-small text-n9 bg-n1 rounded-chip px-3 py-2.5 [overflow-wrap:anywhere]">{text}</code>
      <IconButton label="Copy" onClick={() => { void navigator.clipboard?.writeText(text); toast('Copied'); }}><CopyIcon size={16} /></IconButton>
    </div>
  );
}

// ---------------------------------------------------------------- data model

const readRebuild = (p: URLSearchParams) => p.get('rebuild') || undefined;
const withRebuild = (p: URLSearchParams, v: string | undefined) => { const out = new URLSearchParams(p); if (v) out.set('rebuild', v); else out.delete('rebuild'); return out; };
const readDetails = (p: URLSearchParams) => p.get('details') || undefined;
const withDetails = (p: URLSearchParams, v: string | undefined) => { const out = new URLSearchParams(p); if (v) out.set('details', v); else out.delete('details'); return out; };
const STATUS_FILTER_LABEL: Record<ClassStatusFilter, string> = { all: 'All', new: 'New', checked: 'Checked', deprecated: 'Deprecated' };
/** What the chips of a facet say. */
const SENS_CHIP: Record<Sensitivity, string> = { personal: 'Personal', confidential: 'Confidential', internal: 'Internal', none: 'Not sensitive' };
function facetLabel(f: Facet, key: string): string {
  if (key === NO_GROUP) return f === 'domain' ? 'No domain' : f === 'sens' ? 'Not set' : 'No system';
  return f === 'sens' ? SENS_CHIP[key as Sensitivity] ?? key : f === 'sys' ? systemName(key) : key;
}
const FACET_TITLE: Record<Facet, string> = { domain: 'Domain', sens: 'Sensitivity', sys: 'System' };

export function ModelSettings() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { link, envs, current, select, visible } = useEnv();
  const tid = tenant?.id ?? '';
  const model = useAsync(() => Api.model(tid), [tid, current?.id]);
  // The view is in the URL, so Back from the data search returns to it; every view switch (a filter, a sort, the actual data
  // and its document) is a history step, so Back undoes it. Typing (the field filter, the value filter) replaces the entry.
  const view = readView(params);
  const location = useLocation();
  const change = (patch: Partial<ModelView>, how: 'push' | 'replace' = 'push') =>
    setParams((p) => patchView(p, patch), how === 'replace' ? { replace: true, state: location.state } : undefined);
  // The values window is a step in history: Back closes it, a sort inside it is a step of its own, and closing it goes back
  // past them (or replaces when linked to directly)
  const valuesWin = useModalSteps('valuesOpened');
  const openValues = (path: string) => valuesWin.open((p) => patchView(p, { vf: path, vq: '', vn: VALUE_PAGE }));
  const closeValues = () => valuesWin.close((p) => patchView(p, { vf: undefined, vq: '', vn: VALUE_PAGE }));
  // "Add to my data model…" is a step in history too (?addto=<field path>), see lib/urlmodal.ts
  const addTo = useUrlModal(ADD_KEY, readAdd, withAdd);
  const rebuildModal = useUrlModal('rebuild', readRebuild, withRebuild);
  const [modelsVersion, setModelsVersion] = useState(0);
  // Two tabs: the discovered data model and your own data models; switching is a step in history
  const tab = readTab(params);
  const switchTab = (t: SettingsTab) => { if (t !== tab) setParams((p) => withTab(p, t)); };
  const own = useAsync(() => Api.virtualModels(tid), [tid, modelsVersion]);
  const [ownCount, setOwnCount] = useState<number>();
  // The catalog: display names, metadata and which data models are new (lib/catalogs.tsx)
  const catalog = useCatalog();
  const details = useUrlModal('details', readDetails, withDetails);
  const elsewhere = useNewElsewhere();
  // A model read creates the catalog rows of classes seen for the first time: read the catalog again after it
  const { reload: reloadCatalog } = catalog;
  useEffect(() => { if (model.data) void reloadCatalog(); }, [model.data, reloadCatalog]);
  const selected = view.cls, showExamples = view.examples;
  const [filter, setFilter] = useSyncedText(view.filter, (t) => change({ filter: t }, 'replace'));
  const [valueQuery, setValueQuery] = useSyncedText(view.vq, (t) => change({ vq: t, vn: VALUE_PAGE }, 'replace'));
  const classes = model.data?.classes ?? [];
  const names = useMemo(() => classes.map((c) => c.name), [classes]);
  const statusCount = useMemo(() => statusCounts(names, catalog.entries), [names, catalog.entries]);
  const toCheck = useMemo(() => newClasses(names, catalog.entries), [names, catalog.entries]);
  // The system of a class: pinned, or as detected (the Cloud asks the Vaults; the System chips show once it has answered)
  const systemsData = useAsync(() => Api.systems(tid), [tid, current?.id]);
  const systemsKnown = !!systemsData.data;
  const systemOfClass = (c: string) => catalog.entries.get(c)?.system ?? systemsData.data?.classes[c]?.system ?? null;
  // The chips (in the URL with replace): OR within a facet, AND across; the System chips wait for the systems
  const facetSel = { ...facetsOf(view), sys: systemsKnown ? view.sys : [] };
  // (cheap: a pass over the classes per facet)
  const facets = classFacets(names, catalog.entries, view.status, facetSel, systemOfClass, facetLabel);
  // A facet row shows when the classes have at least two of its values (unfiltered), so rows do not come and go while choosing
  const facetsAll = classFacets(names, catalog.entries, 'all', {}, systemOfClass);
  const listed = listClasses(classes, catalog.entries, view.status, facetSel, systemOfClass);
  // The class asked for (also when the filter hides it), else the one with the most fields of those listed (of all when none is)
  const widest = <T extends { fields: unknown[] }>(xs: readonly T[]) => [...xs].sort((a, b) => b.fields.length - a.fields.length)[0];
  const cls = classes.find((c) => c.name === selected) ?? widest(listed) ?? widest(classes);
  const info: CatalogEntry | undefined = cls ? catalog.entries.get(cls.name) : undefined;
  const [confirmAll, setConfirmAll] = useState(false);
  const markStatus = async (list: string[], status: CatalogStatus) => {
    // The class looked at stays in view when the status filter would no longer pick it
    if (cls && !view.cls && list.includes(cls.name)) change({ cls: cls.name }, 'replace');
    try {
      // Marking as checked accepts the suggestions too (domain, sensitivity, tags where nothing is set): the UI says so
      let accepted = 0;
      const withSuggestions = status === 'checked' ? list.filter((c) => hasSuggestions(catalog.entries.get(c))) : [];
      if (withSuggestions.length) {
        const a = await Api.acceptSuggestions(tid, withSuggestions);
        catalog.applyMany(a.changed);
        accepted = a.changed.length;
      }
      const r = await Api.setCatalogStatus(tid, list, status);
      catalog.applyStatus(list, status);
      const n = r.changed.length;
      const plus = accepted ? ` · suggestions accepted${n === 1 && accepted === 1 ? '' : ` for ${count(accepted)}`}` : '';
      toast((status === 'checked' ? (n === 1 ? `${cap(noun(r.changed[0]!, 2))} marked as checked` : `${count(n)} data models marked as checked`)
        : n === 1 ? `${cap(noun(r.changed[0]!, 2))} marked as ${STATUS_LABEL[status].toLowerCase()}` : `${count(n)} data models marked as ${STATUS_LABEL[status].toLowerCase()}`) + plus);
    } catch (e) {
      toast(message(e), 'fail');
    }
  };
  const accept = async (list: string[]) => {
    try {
      const a = await Api.acceptSuggestions(tid, list);
      catalog.applyMany(a.changed);
      toast(a.changed.length ? `Suggestions accepted for ${a.changed.length === 1 ? noun(a.changed[0]!.class, 2) : `${count(a.changed.length)} data models`}` : 'Nothing to accept');
    } catch (e) {
      toast(message(e), 'fail');
    }
  };
  // Grouping of the class list (in the URL with replace, like the filters)
  const groups = groupClasses(listed, catalog.entries, view.group, systemOfClass, systemName);
  const folded = new Set(view.fold);
  const toggleFold = (key: string) => change({ fold: folded.has(key) ? view.fold.filter((k) => k !== key) : [...view.fold, key] });
  const toCheckSuggested = toCheck.filter((c) => hasSuggestions(catalog.entries.get(c))).length;
  const chooseClass = (name: string) => { if (name !== view.cls) setParams((p) => patchView(p, { cls: name, index: 0, find: undefined, ff: undefined, show: 'all', rows: FIELD_PAGE, vf: undefined })); };
  const examples = useExamples(tid, cls?.name, view, change);
  // Fields chosen to be forgotten (kept per data model for the session, so Back brings the choice back)
  const { canManage } = useAuth();
  const toast = useToast();
  const [forgetDraft, setForgetDraft, clearForgetDraft] = useFormDraft(`forget.${tid}.${cls?.name ?? ''}`, { paths: [] as string[] });
  const [forgetting, setForgetting] = useState(false);
  const [busy, setBusy] = useState(false);
  const chosen = useMemo(() => new Set(forgetDraft.paths), [forgetDraft.paths]);
  // Each field with how common it is; the list is ordered (most common first by default) and can be narrowed
  const rated = useMemo(() => (cls?.fields ?? []).map((f) => ({ f, s: statOf(f, cls!) })), [cls]);
  const counts = useMemo(() => kindCounts(rated.map((r) => r.s)), [rated]);
  // Leftovers: in only a few documents AND not seen for a long time
  const leftovers = useMemo(() => rated.filter((r) => r.s.kind === 'rare' && r.s.stale).map((r) => r.f.path), [rated]);
  const fieldsAll = useMemo(() => {
    const q = filter.toLowerCase();
    const list = rated.filter((r) => matchesKind(r.s, view.show) && (!q || r.f.path.toLowerCase().includes(q)));
    const by: Record<string, (a: typeof list[number], b: typeof list[number]) => number> = {
      coverage: (a, b) => b.s.coverage - a.s.coverage || a.f.path.localeCompare(b.f.path),
      name: (a, b) => a.f.path.localeCompare(b.f.path),
      count: (a, b) => b.f.count - a.f.count || a.f.path.localeCompare(b.f.path),
      last: (a, b) => (b.f.lastSeen ?? 0) - (a.f.lastSeen ?? 0),
    };
    return [...list].sort(by[view.sort]);
  }, [rated, filter, view.show, view.sort]);
  const fields = fieldsAll.slice(0, view.rows).map((r) => r.f);
  const toggle = (path: string) => setForgetDraft({ paths: chosen.has(path) ? forgetDraft.paths.filter((x) => x !== path) : [...forgetDraft.paths, path] });
  const forget = async () => {
    if (!cls) return;
    setBusy(true);
    try {
      const r = await Api.forgetFields(tid, cls.name, forgetDraft.paths);
      const failed = r.vaults.filter((v) => v.error);
      const removed = Math.max(0, ...r.vaults.map((v) => v.removed));
      toast(failed.length ? `Forgotten in ${r.vaults.length - failed.length} Vault(s); ${failed[0]!.vaultName}: ${failed[0]!.error}` : `${removed} field${removed === 1 ? '' : 's'} forgotten${r.offline.length ? `; not done in offline Vault(s): ${r.offline.join(', ')}` : ''}`, failed.length ? 'fail' : undefined);
      clearForgetDraft();
      model.reload();
    } catch (e) {
      toast(message(e), 'fail');
    } finally {
      setBusy(false);
      setForgetting(false);
    }
  };
  const fieldsByPath = useMemo(() => new Map((cls?.fields ?? []).map((f) => [f.path, f])), [cls]);
  const statByPath = useMemo(() => new Map(rated.map((r) => [r.f.path, r.s])), [rated]);

  const stats: [string, string, boolean][] = cls ? [
    ['Documents', count(cls.messages), false], ['Fields', count(cls.fields.length), false],
    ['Parse errors', count(cls.parseErrors), cls.parseErrors > 0], ['Last document', ago(cls.lastSeen), false],
  ] : [];
  return (
    <>
      <Title action={(
        <div className="flex items-center gap-4 flex-wrap justify-end">
          {visible && current && (
            <label className="flex items-center gap-2 text-small text-n8">
              Environment
              <EnvPicker envs={envs} value={current.id} onChange={select} />
            </label>
          )}
        </div>
      )}>Data model</Title>
      <div role="tablist" aria-label="Data models" className="flex items-center gap-1 mb-5 border-b border-n4">
        {([['data', 'Data model', toCheck.length, 'new'], ['yours', 'Your data models', ownCount ?? own.data?.length, undefined]] as const).map(([id, label, n, what]) => (
          <button key={id} type="button" role="tab" id={`tab-${id}`} aria-selected={tab === id} aria-controls={`panel-${id}`} onClick={() => switchTab(id)}
            className={cx('relative h-10 px-3 -mb-px border-0 border-b-2 bg-transparent cursor-pointer text-small flex items-center gap-2 transition-colors duration-[var(--t-micro)]',
              tab === id ? 'border-accent text-n10 font-medium' : 'border-transparent text-n8 hover:text-n10')}>
            {label}
            {n !== undefined && (n > 0 || !what) && (
              <span className={cx('min-w-5 h-5 px-1.5 rounded-full text-micro tabular-nums grid place-items-center', what ? 'bg-accent-soft text-accent font-semibold' : 'bg-n3 text-n8')}
                title={what ? `${count(n)} new to check` : undefined}>{count(n)}{what ? <span className="sr-only"> new</span> : null}</span>
            )}
          </button>
        ))}
      </div>
      {tab === 'yours' ? (
        <div role="tabpanel" id="panel-yours" aria-labelledby="tab-yours">
          <VirtualModels tid={tid} reloadKey={modelsVersion} embedded onCount={setOwnCount} classes={model.data?.classes} />
        </div>
      ) : (
      <div role="tabpanel" id="panel-data" aria-labelledby="tab-data">
      <p className="m-0 mb-5 text-small text-n8 max-w-3xl">
        Data models and their fields are discovered from incoming data on all vaults; nothing is defined in advance. A type is inferred once at least 20 values agree (95 %); values of another type are still stored and searchable as text.
      </p>
      {model.loading && !model.data && <Spinner />}
      {model.error && <p className="m-0 mb-4 text-small text-fail">{model.error}</p>}
      {model.data && classes.length === 0 && <Surface className="p-10 text-center text-small text-n8">No data yet.</Surface>}
      {toCheck.length === 0 && elsewhere.length > 0 && model.data && (
        <div role="status" aria-label="New data models elsewhere" className="mb-5 flex items-center gap-3 flex-wrap rounded-card bg-n2 shadow-node px-4 py-3 text-small">
          <span className="text-n9">No new data models here</span>
          <NewElsewhere items={elsewhere} prefix="·" className="text-small" />
        </div>
      )}
      {toCheck.length > 0 && (
        <div role="status" aria-label="New data models" className="mb-5 flex items-center gap-3 flex-wrap rounded-card bg-accent-soft px-4 py-3 text-small">
          <span className="size-2 rounded-full bg-accent shrink-0" aria-hidden="true" />
          {confirmAll ? (
            <>
              <span className="flex-1 min-w-[220px] text-n10">Mark {toCheck.length === 1 ? `${noun(toCheck[0]!, 2)}` : `all ${count(toCheck.length)} new data models`} as checked?{' '}
                <span className="text-n8">They stop showing as new; a data model that arrives later is new again.
                  {toCheckSuggested > 0 && <> The suggested domain, sensitivity and tags of {toCheckSuggested === toCheck.length ? (toCheck.length === 1 ? 'it' : 'them') : `${count(toCheckSuggested)} of them`} are accepted too, where nothing is set.</>}</span></span>
              <Button variant="ghost" onClick={() => setConfirmAll(false)}>Cancel</Button>
              <Button variant="primary" data-action="mark-all-checked" onClick={() => { setConfirmAll(false); void markStatus(toCheck, 'checked'); }}>{toCheckSuggested > 0 ? 'Accept suggestions & mark as checked' : 'Mark as checked'}</Button>
            </>
          ) : (
            <>
              <span className="flex-1 min-w-[220px] text-n10"><span className="font-semibold">{count(toCheck.length)} new data model{toCheck.length === 1 ? '' : 's'} to check</span>
                <span className="text-n8"> · arrived with the data{canManage ? ': give them a name and details, or mark them as checked' : ''}</span>
                {elsewhere.length > 0 && <NewElsewhere items={elsewhere} prefix="· more:" className="inline-flex ml-1.5 text-small align-baseline" />}</span>
              {view.status !== 'new' && <Button onClick={() => change({ ...CLEAR_FILTERS, status: 'new' })}>Show them</Button>}
              {canManage && <Button variant="ghost" onClick={() => setConfirmAll(true)}>Mark all as checked…</Button>}
            </>
          )}
        </div>
      )}
      {cls && (
        <div className="mb-4 flex flex-col gap-2.5" aria-label="Filter data models" role="region">
          <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
            <div className="flex items-center gap-0.5 flex-wrap" role="group" aria-label="Show data models">
              {CLASS_STATUS_FILTERS.map((k) => (
                <button key={k} type="button" aria-pressed={view.status === k} onClick={() => change({ status: k })}
                  disabled={k !== 'all' && k !== view.status && statusCount[k] === 0}
                  className={cx('h-7 px-2.5 rounded-full border-0 cursor-pointer text-small whitespace-nowrap transition-colors duration-[var(--t-micro)] disabled:opacity-40 disabled:cursor-default',
                    view.status === k ? 'bg-accent-soft text-accent font-medium' : 'bg-transparent text-n8 hover:bg-n3 hover:text-n10')}>
                  {STATUS_FILTER_LABEL[k]}{statusCount[k] > 0 && <span className={cx('tabular-nums ml-1', view.status === k ? 'text-accent' : 'text-n7')}>{count(statusCount[k])}</span>}
                </button>
              ))}
            </div>
            {isFiltered(view) && (
              <button type="button" data-action="clear-filters" onClick={() => change(CLEAR_FILTERS)}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-full border-0 bg-transparent cursor-pointer text-small text-n8 hover:bg-n3 hover:text-n10">
                <CloseIcon size={12} />Clear filters
              </button>
            )}
            <Segmented className="ml-auto" label="Group" ariaLabel="Group by" value={view.group}
              options={GROUP_BYS.map((g) => [g, GROUP_LABEL[g]] as const)} onChange={(g) => change({ group: g, fold: [] })} />
          </div>
          {(['domain', 'sens', 'sys'] as const).map((f) => {
            const values = facets[f];
            const chosen = view[f];
            if (f === 'sys' && !systemsKnown) return null;
            if (facetsAll[f].length < 2 && chosen.length === 0) return null;
            return (
              <FacetRow key={f} facet={f} label={FACET_TITLE[f]} values={values} chosen={chosen} limit={f === 'sens' ? 8 : 6}
                chip={(v: FacetValue) => {
                  const on = chosen.includes(v.key);
                  const allSuggested = f === 'domain' && v.suggested > 0 && v.suggested === v.count;
                  return (
                    <FacetChip chosen={on} count={v.count} dashed={allSuggested} data-facet={f} data-key={v.key} className={f === 'sys' && v.key !== NO_GROUP ? (on ? '!pl-2' : '!pl-1') : undefined}
                      title={v.suggested > 0 ? `${count(v.suggested)} of ${count(v.count)} only by a suggestion nobody has accepted yet` : undefined}
                      onClick={() => change(toggleFacet(view, f, v.key))}>
                      {f === 'sys' && v.key !== NO_GROUP && <SystemBadge code={v.key} size={20} />}
                      {f === 'sens' && (v.key === 'personal' || v.key === 'confidential') && <i className={cx('size-1.5 rounded-full shrink-0', v.key === 'personal' ? 'bg-warn' : 'bg-n7')} aria-hidden="true" />}
                      {!(f === 'sys' && facetLabel(f, v.key).toUpperCase() === systemCode(v.key)) && <span className={cx('truncate', v.key === NO_GROUP && !on && 'text-n8 italic')}>{facetLabel(f, v.key)}</span>}
                    </FacetChip>
                  );
                }} />
            );
          })}
          {view.sys.length > 0 && !systemsKnown && <span className="text-micro text-n7">Finding the systems in the data…</span>}
        </div>
      )}
      {cls && (
        <div className="grid gap-5 grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)]">
          <div className="flex flex-col gap-2 self-start min-w-0">
            {view.group === 'system' && !systemsKnown && systemsData.loading && <span className="text-micro text-n7 px-1">Finding the systems in the data…</span>}
            <Surface className="p-1.5 max-h-[70vh] overflow-auto">
              {listed.length === 0 && (
                <div className="p-4 flex flex-col items-center gap-2 text-small text-n7 text-center">
                  <span>{view.status === 'new' && !isFiltered({ ...view, status: 'all' }) ? 'Nothing new to check.' : 'No data model matches.'}</span>
                  <Button variant="ghost" onClick={() => change(CLEAR_FILTERS)}>Show all</Button>
                </div>
              )}
              {groups.map((g) => {
                const open = view.group === 'none' || !folded.has(g.key);
                const items = open ? g.items : [];
                return (
                  <div key={g.key} role={view.group === 'none' ? undefined : 'group'} aria-label={view.group === 'none' ? undefined : g.label}>
                    {view.group !== 'none' && (
                      <button type="button" aria-expanded={open} data-group={g.key} onClick={() => toggleFold(g.key)}
                        className="w-full flex items-center gap-1.5 px-2 pt-2.5 pb-1 border-0 bg-transparent cursor-pointer text-left text-micro font-semibold uppercase tracking-wide text-n7 hover:text-n10">
                        <ChevronIcon size={12} className={cx('shrink-0 transition-transform duration-[var(--t-micro)]', !open && '-rotate-90')} />
                        <span className={cx('truncate', view.group === 'sensitivity' && g.key === 'personal' && 'text-warn')}>{g.label}</span>
                        <span className="tabular-nums font-normal normal-case tracking-normal">{count(g.items.length)}</span>
                        {g.suggested > 0 && <span className="ml-auto font-normal normal-case tracking-normal text-n7" title="Grouped here by a suggestion nobody has accepted yet">{g.suggested === g.items.length ? 'suggested' : `${count(g.suggested)} suggested`}</span>}
                      </button>
                    )}
              {items.map((c) => {
                const st = statusOf(catalog.entries, c.name);
                const e = catalog.entries.get(c.name);
                const dom = view.group === 'domain' ? null : domainOf(e);
                const sens = view.group === 'sensitivity' ? null : sensitivityOf(e);
                const showSens = sens && (sens.value === 'personal' || sens.value === 'confidential') ? sens : null;
                return (
                  <button key={c.name} type="button" onClick={() => chooseClass(c.name)} aria-pressed={c.name === cls.name} data-class={c.name}
                    className={cx('w-full text-left px-3 py-2 rounded-chip cursor-pointer border-0 flex items-start justify-between gap-2 transition-colors duration-[var(--t-micro)]',
                      c.name === cls.name ? 'bg-accent-soft' : 'bg-transparent hover:bg-n3', st === 'deprecated' && 'opacity-60')}>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="block text-small font-medium text-n10 truncate">{cap(noun(c.name, 2))}</span>
                        {st === 'new' && <StatusChip status="new" />}
                        {st === 'deprecated' && <StatusChip status="deprecated" />}
                      </span>
                      <span className="block font-mono text-micro text-n7 truncate">{c.name}</span>
                      <span className="block text-micro text-n7">{count(c.messages)} documents · {c.fields.length} fields</span>
                      {(dom || showSens) && (
                        <span className="flex items-center gap-1 mt-1 min-w-0 flex-wrap">
                          {dom && <CatalogValue size="xs" suggested={dom.suggested} title={dom.suggested && e?.suggestions ? suggestionTitle(e.suggestions, 'domain') : 'Domain'}>{dom.value}</CatalogValue>}
                          {showSens && <CatalogValue size="xs" suggested={showSens.suggested} personal={showSens.value === 'personal'}
                            title={showSens.suggested && e?.suggestions ? suggestionTitle(e.suggestions, 'sensitivity') : 'Sensitivity'}>{SENSITIVITY_LABEL[showSens.value]}</CatalogValue>}
                        </span>
                      )}
                      {c.parseErrors > 0 && (
                        <span className="flex items-center gap-1.5 text-micro text-n8 whitespace-nowrap">
                          <span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />{count(c.parseErrors)} parse errors
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
                  </div>
                );
              })}
            </Surface>
          </div>
          <div className="flex flex-col gap-4 min-w-0">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{cap(noun(cls.name, 2))}</h2>
                  {info && info.status !== 'checked' && <StatusChip status={info.status} />}
                </div>
                <span className="text-micro text-n7"><span className="font-mono">{cls.name}</span>{info && info.displayName !== info.derived.displayName ? <> · one is “{info.displayName}”</> : null}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {canManage && info?.status === 'new' && (
                  <Button data-action="mark-checked" onClick={() => void markStatus([cls.name], 'checked')}
                    title={hasSuggestions(info) ? 'Marks it as checked and accepts the suggested domain, sensitivity and tags (where nothing is set)' : undefined}>
                    <CheckIcon size={14} />{hasSuggestions(info) ? 'Accept suggestions & mark as checked' : 'Mark as checked'}
                  </Button>
                )}
                {canManage && <Button onClick={() => details.open(cls.name)} disabled={!info}><PencilIcon size={14} />Edit details…</Button>}
                {canManage && <Button onClick={() => rebuildModal.open(cls.name)} title="Reads the stored documents of this data model again and builds its model from them">Rebuild from data…</Button>}
                {canManage && info && (
                  <Menu.Root>
                    <Menu.Trigger aria-label="Status of this data model"
                      className="size-9 rounded-chip grid place-items-center bg-n3 text-n9 border-0 cursor-pointer text-body leading-none hover:bg-n4 focus-visible:ring-2 focus-visible:ring-accent">
                      <span aria-hidden="true">⋯</span>
                    </Menu.Trigger>
                    <Menu.Content align="end" className="min-w-64">
                      <Menu.Label>Status: {STATUS_LABEL[info.status]}</Menu.Label>
                      {info.status !== 'checked' && <Menu.Item icon={<CheckIcon size={16} />} onSelect={() => void markStatus([cls.name], 'checked')}>{hasSuggestions(info) ? 'Accept suggestions & mark as checked' : 'Mark as checked'}</Menu.Item>}
                      {info.status !== 'deprecated' && <Menu.Item onSelect={() => void markStatus([cls.name], 'deprecated')}>Mark as deprecated</Menu.Item>}
                      {info.status !== 'new' && <Menu.Item onSelect={() => void markStatus([cls.name], 'new')}>Back to new</Menu.Item>}
                    </Menu.Content>
                  </Menu.Root>
                )}
              </div>
            </div>
            {info && <CatalogFacts info={info} onAccept={canManage && hasSuggestions(info) ? () => void accept([info.class]) : undefined} />}
            <div className="flex gap-8 flex-wrap">
              {stats.map(([l, v, warn]) => (
                <div key={l} className="flex flex-col gap-0.5">
                  <Kicker>{l}</Kicker>
                  <span className={cx('text-title font-semibold tabular-nums', warn && 'text-warn')}>{v}</span>
                </div>
              ))}
            </div>
            <ExampleBar tid={tid} ex={examples} on={showExamples} setOn={(v) => change({ examples: v, index: 0, find: undefined })} />
            <label className="flex items-center gap-2 h-9 px-3 rounded-chip bg-n3 text-small text-n8 w-full max-w-xs">
              <FilterIcon size={14} /><span className="sr-only">Filter fields</span>
              <input className="flex-1 min-w-0 bg-transparent border-0 outline-none text-n10 placeholder:text-n7" placeholder="Filter fields" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </label>
            {canManage && leftovers.length > 0 && (
              <div role="status" className="flex items-center gap-3 flex-wrap rounded-chip bg-n3 px-3 py-2 text-small text-n9">
                <span className="size-1.5 rounded-full bg-warn shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-[240px]">
                  {leftovers.length === 1 ? '1 field looks like a leftover' : `${count(leftovers.length)} fields look like leftovers`}: found in only a few documents and not seen for a long time.
                </span>
                <Button onClick={() => { setForgetDraft({ paths: leftovers }); change({ show: 'rare', rows: FIELD_PAGE }); }}>Select {count(leftovers.length)}</Button>
              </div>
            )}
            {canManage && forgetDraft.paths.length > 0 && !forgetting && (
              <div className="flex items-center gap-3 flex-wrap rounded-chip bg-accent-soft px-3 py-2 text-small">
                <span className="flex-1 min-w-[200px] text-n10">{count(forgetDraft.paths.length)} selected</span>
                <Button variant="ghost" onClick={clearForgetDraft}>Clear selection</Button>
                <Button variant="danger" onClick={() => setForgetting(true)}>Forget from the model…</Button>
              </div>
            )}
            {forgetting && (
              <Confirm question={`Forget ${count(forgetDraft.paths.length)} field${forgetDraft.paths.length === 1 ? '' : 's'} from ${noun(cls.name, 2)}?`} action={busy ? 'Working…' : 'Forget fields'}
                onCancel={() => setForgetting(false)} onConfirm={() => void forget()}>
                They leave the data model in every online Vault of this environment. The data stays indexed and searchable, and a field returns to the model if new documents have it.
              </Confirm>
            )}
            {cls.fields.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Show fields">
                {KIND_FILTERS.map((k) => (
                  <button key={k} type="button" aria-pressed={view.show === k} onClick={() => change({ show: k, rows: FIELD_PAGE })}
                    disabled={k !== 'all' && counts[k] === 0}
                    className={cx('h-8 px-3 rounded-full border-0 cursor-pointer text-small transition-colors duration-[var(--t-micro)] disabled:opacity-40 disabled:cursor-default',
                      view.show === k ? 'bg-accent-soft text-accent font-medium' : 'bg-n3 text-n9 hover:bg-n4')}>
                    {FILTER_LABEL[k]} <span className="tabular-nums text-n7">{count(counts[k])}</span>
                  </button>
                ))}
                <span className="text-micro text-n7">Common = in at least half of the documents · Rare = under 5 % or a handful · Stale = stopped while the data model goes on</span>
              </div>
            )}
            <Surface className="overflow-x-auto" data-hscroll>
              <table className={cx('w-full border-collapse', showExamples ? 'min-w-[900px]' : 'min-w-[760px]')}>
                <thead>
                  <tr>
                    {canManage && <th className={cx(th, 'w-8')}><span className="sr-only">Select</span></th>}
                    <SortTh id="name" view={view} change={change} className="min-w-56">Field</SortTh>
                    {showExamples && <th className={cx(th, EXAMPLE_TINT, 'min-w-60')}>Actual data</th>}
                    <th className={th}>Type</th><th className={th}>Observed</th>
                    <SortTh id="coverage" view={view} change={change} className="min-w-40">In documents</SortTh>
                    <SortTh id="count" view={view} change={change} className="text-right">Count</SortTh><th className={cx(th, 'text-right')}>Empty</th><th className={cx(th, 'text-right')}>Type errors</th>
                    <SortTh id="last" view={view} change={change}>Last seen</SortTh>
                    <th className={th}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => {
                    const entries = Object.entries(f.types).filter(([, n]) => n) as [string, number][];
                    const total = entries.reduce((s, [, n]) => s + n, 0);
                    return (
                      <tr key={f.path} className={cx('hover:bg-n3/60', chosen.has(f.path) && 'bg-accent-soft/40')}>
                        {canManage && (
                          <td className={cx(td, 'w-8')}>
                            <input type="checkbox" aria-label={`Select ${f.path}`} checked={chosen.has(f.path)} onChange={() => toggle(f.path)} className="size-4 cursor-pointer accent-[var(--accent)]" />
                          </td>
                        )}
                        <td className={cx(td, 'font-mono text-small text-n10 [overflow-wrap:anywhere]')}>{f.path}</td>
                        {showExamples && (
                          <td className={cx(td, EXAMPLE_TINT, 'max-w-72 text-small text-n10 [overflow-wrap:anywhere]')}>
                            {examples.values.has(f.path) ? <Marked text={examples.values.get(f.path)!} term={examples.lookup} /> : <span className="text-n6">—</span>}
                          </td>
                        )}
                        <td className={cx(td, 'whitespace-nowrap')}>{f.effectiveType ? <span className="font-mono text-micro text-n8 uppercase">{f.effectiveType}</span> : <span className="text-micro text-n7">not enough values yet</span>}</td>
                        <td className={cx(td, 'text-micro text-n8 whitespace-nowrap')}>
                          {total ? entries.sort((a, b) => b[1] - a[1]).map(([t, n]) => `${t.toLowerCase()} ${Math.round((n / total) * 100)} %`).join(' · ') : 'only empty'}
                        </td>
                        <td className={cx(td, 'whitespace-nowrap')}><Coverage s={statByPath.get(f.path)} /></td>
                        <td className={cx(td, 'text-right font-mono tabular-nums text-n9')}>{count(f.count)}</td>
                        <td className={cx(td, 'text-right font-mono tabular-nums text-n7')}>{f.empty ? count(f.empty) : ''}</td>
                        <td className={cx(td, 'text-right font-mono tabular-nums text-n9')}>
                          {f.typeErrors ? <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />{count(f.typeErrors)}</span> : ''}
                        </td>
                        <td className={cx(td, 'text-n7 whitespace-nowrap')}>
                          {ago(f.lastSeen)}
                          {statByPath.get(f.path)?.stale && <span className="ml-2 inline-flex items-center gap-1 text-micro text-warn" title={`The data model has newer documents than this field's last one by ${duration(statByPath.get(f.path)!.staleFor)}`}><span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />stopped</span>}
                        </td>
                        <td className={cx(td, 'text-right py-1')}>
                          <Menu.Root>
                            <Menu.Trigger aria-label={`Actions for ${f.path}`}
                              className="size-8 rounded-full grid place-items-center bg-transparent text-n8 border-0 cursor-pointer text-body leading-none hover:bg-n3 focus-visible:ring-2 focus-visible:ring-accent">
                              <span aria-hidden="true">⋯</span>
                            </Menu.Trigger>
                            <Menu.Content align="end" className="min-w-64">
                              <Menu.Item icon={<FilterIcon size={16} />} onSelect={() => openValues(f.path)}>
                                Show distinct values
                              </Menu.Item>
                              {canManage && (
                                <Menu.Item icon={<SchemaIcon size={16} />} onSelect={() => addTo.open(f.path)}>
                                  Add to my data model…
                                </Menu.Item>
                              )}
                              <Menu.Separator />
                              <Menu.Label>Opens the data search, leaving Settings</Menu.Label>
                              {f.typeErrors > 0 && (
                                <Menu.Item icon={<SearchIcon size={16} />}
                                  onSelect={() => void navigate(link(`/s?${new URLSearchParams({ q: `@class=${quote(cls.name)} @typeError=${f.path}`, range: 'all' })}`))}>
                                  Search documents with type errors ({count(f.typeErrors)})
                                </Menu.Item>
                              )}
                              <Menu.Item icon={<SearchIcon size={16} />}
                                onSelect={() => void navigate(link(`/s?${new URLSearchParams({ q: `@class=${quote(cls.name)} ${f.path}=*`, range: 'all' })}`))}>
                                Search data with this field
                              </Menu.Item>
                            </Menu.Content>
                          </Menu.Root>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {cls.fields.length === 0 && <div className="p-6 text-small text-n7 text-center">No fields: the payloads of this data model are not XML or JSON (text payloads are stored and searchable by their metadata).</div>}
              {cls.fields.length > 0 && fieldsAll.length === 0 && (
                <div className="p-6 flex flex-col items-center gap-3 text-small text-n7 text-center">
                  <span>
                    {view.show !== 'all' && counts[view.show] === 0
                      ? `No ${FILTER_LABEL[view.show].toLowerCase()} fields in ${noun(cls.name, 2)}${filter ? ` matching “${filter}”` : ''}.`
                      : `No field matches “${filter}”.`}
                  </span>
                  <Button onClick={() => { setFilter(''); change({ show: 'all', filter: '', rows: FIELD_PAGE }); }}>Show all {count(cls.fields.length)} fields</Button>
                </div>
              )}
              {fieldsAll.length > fields.length && (
                <div className="p-3 flex justify-center">
                  <Button onClick={() => change({ rows: view.rows + FIELD_PAGE }, 'replace')}>Show {count(Math.min(FIELD_PAGE, fieldsAll.length - fields.length))} more of {count(fieldsAll.length - fields.length)} fields</Button>
                </div>
              )}
            </Surface>
          </div>
        </div>
      )}
      {view.vf && cls && fieldsByPath.get(view.vf) && (
        <DistinctValues tid={tid} cls={cls.name} path={view.vf} fieldCount={fieldsByPath.get(view.vf)!.count}
          view={{ vs: view.vs, vq: valueQuery, vn: view.vn }}
          onView={(patch) => (patch.vs !== undefined ? valuesWin.step((p) => patchView(p, patch)) : change(patch, 'replace'))} onQuery={setValueQuery} onClose={closeValues}
          onActual={(value) => setParams((p) => patchView(p, { vf: undefined, vq: '', vn: VALUE_PAGE, examples: true, index: 0, find: value, ff: view.vf }), { state: {} })}
          onSearch={(value) => void navigate(link(`/s?${new URLSearchParams({ q: `@class=${quote(cls.name)} ${view.vf}="${value.replace(/"/g, '')}"`, range: 'all' })}`))} />
      )}
      {canManage && addTo.value && cls && fieldsByPath.get(addTo.value) && (
        <AddToModel tid={tid} cls={cls.name} path={addTo.value} leaf={fieldsByPath.get(addTo.value)!.leaf} onClose={addTo.close}
          onSaved={(saved, name) => {
            setModelsVersion((v) => v + 1);
            addTo.close();
            toast(`Added to ${name} (draft)`, 'ok', { label: 'Open', onClick: () => void navigate(link(`/settings/model?${new URLSearchParams({ model: saved.id, tab: 'yours' })}`), { state: { reveal: true } }) });
          }} />
      )}
      {rebuildModal.value && canManage && (
        <RebuildModel tid={tid} scope={rebuildModal.value === '1' ? ALL : rebuildModal.value} label={cls ? noun(cls.name, 2) : 'the data model'}
          onScope={(scope) => setParams((p) => withRebuild(p, scope || cls?.name), { replace: true, state: location.state })}
          onClose={rebuildModal.close} onDone={() => model.reload()} />
      )}
      {details.value && canManage && catalog.entries.get(details.value) && (
        <CatalogEditor key={details.value} tid={tid} entry={catalog.entries.get(details.value)!} domains={catalog.domains} tags={catalog.tags}
          onClose={details.close}
          onSaved={(saved) => {
            catalog.apply(saved);
            details.close();
            toast(`${cap(saved.displayPlural)} saved`);
          }} />
      )}
      </div>
      )}
    </>
  );
}

export const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** "New" (accent) or "Deprecated" (muted): the status of a data model where it needs attention. */
function StatusChip({ status }: { status: CatalogStatus }) {
  return (
    <span className={cx('inline-flex items-center h-5 px-2 rounded-full text-micro font-medium whitespace-nowrap shrink-0',
      status === 'new' ? 'bg-accent-soft text-accent ring-1 ring-inset ring-accent/30' : status === 'deprecated' ? 'bg-n3 text-n7' : 'bg-ok-soft text-ok')}>{STATUS_LABEL[status]}</span>
  );
}

/**
 * What the admins have written about a data model: description, domain, tags, steward, sensitivity, the review — and,
 * where they have set nothing, what is suggested (dashed, with the reasons as tooltip; personal data as a callout).
 */
function CatalogFacts({ info, onAccept }: { info: CatalogEntry; onAccept?: () => void }) {
  const facts: [string, ReactNode][] = [];
  const s = info.suggestions;
  const dom = domainOf(info), sens = sensitivityOf(info);
  if (dom) facts.push(['Domain', dom.suggested && s ? <CatalogValue suggested title={suggestionTitle(s, 'domain')}>{dom.value}</CatalogValue> : dom.value]);
  if (info.steward) facts.push(['Steward', info.steward]);
  if (sens) {
    facts.push(['Sensitivity', sens.suggested && s
      ? <CatalogValue suggested personal={sens.value === 'personal'} title={suggestionTitle(s, 'sensitivity')}>{SENSITIVITY_LABEL[sens.value]}</CatalogValue>
      : sens.value === 'personal' ? <CatalogValue personal>{SENSITIVITY_LABEL[sens.value]}</CatalogValue> : SENSITIVITY_LABEL[sens.value]]);
  }
  const suggestedTags = info.tags.length === 0 ? s?.tags ?? [] : [];
  const personal = s?.sensitivity === 'personal' ? s : null;
  const reviewed = info.reviewedAt ? `${info.status === 'deprecated' ? 'Deprecated' : 'Checked'} ${ago(info.reviewedAt)}${info.reviewedBy ? ` by ${info.reviewedBy}` : ''}` : `First seen ${ago(info.firstSeenAt)}`;
  return (
    <div className="flex flex-col gap-2">
      {info.description && <p className="m-0 text-small text-n9 max-w-3xl whitespace-pre-line [overflow-wrap:anywhere]">{info.description}</p>}
      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap text-small">
        {facts.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 min-w-0"><span className="text-n7">{k}</span><span className="text-n10 font-medium truncate">{v}</span></span>
        ))}
        {info.tags.length > 0 && (
          <span className="inline-flex items-center gap-1 flex-wrap" aria-label="Tags">
            {info.tags.map((t) => <span key={t} className="h-6 px-2 rounded-full bg-n3 text-micro text-n9 inline-flex items-center">{t}</span>)}
          </span>
        )}
        {suggestedTags.length > 0 && s && (
          <span className="inline-flex items-center gap-1 flex-wrap" aria-label="Suggested tags">
            {suggestedTags.map((t) => <CatalogValue key={t} suggested title={suggestionTitle(s, 'tags')}>{t}</CatalogValue>)}
          </span>
        )}
        <span className="text-micro text-n7">{reviewed}</span>
        {onAccept && (
          <button type="button" onClick={onAccept} data-action="accept-suggestions"
            className="h-6 px-2 -ml-1 rounded-chip border-0 bg-transparent text-micro font-medium text-accent cursor-pointer hover:bg-n3">Accept suggestions</button>
        )}
      </div>
      {personal && (
        <div role="note" aria-label="Personal data suggested" className="flex items-start gap-2.5 rounded-chip border border-dashed border-warn bg-warn-soft px-3 py-2 text-small max-w-3xl">
          <i className="mt-1.5 size-2 rounded-full bg-warn shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0 text-n10">
            <span className="font-medium">Looks like personal data</span>
            {personal.fields.personal.length > 0 && <span className="text-n9">: {personal.fields.personal.slice(0, 6).join(', ')}{personal.fields.personal.length > 6 ? ` +${personal.fields.personal.length - 6}` : ''}</span>}
            <span className="block text-micro text-n8 mt-0.5">{personal.reasons.sensitivity.join(' · ')}. Suggested — {onAccept ? 'accept it, or set the sensitivity in Edit details…' : 'an owner can accept it'}.</span>
          </span>
        </div>
      )}
    </div>
  );
}

/** Text with the words of the search marked. */
export function Marked({ text, term }: { text: string; term: string | undefined }) {
  return <>{highlightParts(text, term).map((p, i) => (p.hit ? <mark key={i} className="font-semibold text-[1.15em] rounded-md px-1.5 py-0.5 mx-px [box-decoration-break:clone]"
      style={{ background: '#FFE14D', color: '#1B1B1B', boxShadow: '0 0 0 1px #F2C200' }}>{p.text}</mark> : p.text))}</>;
}

const FILTER_LABEL: Record<KindFilter, string> = { all: 'All fields', common: 'Common', occasional: 'Occasional', rare: 'Rare', stale: 'Stale' };

/** A column header that sorts by its column; the sort is in the URL. */
function SortTh({ id, view, change, className, children }: { id: FieldSort; view: ModelView; change: (p: Partial<ModelView>) => void; className?: string; children: ReactNode }) {
  const on = view.sort === id;
  return (
    <th className={cx(th, className)} aria-sort={on ? (id === 'name' ? 'ascending' : 'descending') : undefined}>
      <button type="button" onClick={() => change({ sort: id, rows: FIELD_PAGE })}
        className={cx('bg-transparent border-0 p-0 cursor-pointer uppercase tracking-[inherit] text-[length:inherit] font-[inherit]', on ? 'text-n10' : 'text-inherit hover:text-n10')}>
        {children}{on ? (id === 'name' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
}

/** How many of the documents have the field: a bar, the share and what kind of field it is. */
function Coverage({ s }: { s: FieldStat | undefined }) {
  if (!s) return null;
  return (
    <span className="inline-flex items-center gap-2" title={KIND_LABEL[s.kind]}>
      <span className="w-16 h-1.5 rounded-full bg-n4 overflow-hidden" aria-hidden="true">
        <span className={cx('block h-full rounded-full', s.kind === 'rare' ? 'bg-warn' : 'bg-accent')} style={{ width: `${Math.max(2, Math.round(s.coverage * 100))}%` }} />
      </span>
      <span className="font-mono text-small tabular-nums text-n9 w-10">{percent(s.coverage)}</span>
      <span className={cx('text-micro', s.kind === 'rare' ? 'text-warn' : 'text-n7')}>{s.kind === 'always' ? 'always' : s.kind}</span>
    </span>
  );
}

export const EXAMPLE_TINT = 'bg-accent-soft/40';

/** Example documents of one data model: newest first, or the one document an id was searched for. */
function useExamples(tid: string, cls: string | undefined, view: ModelView, change: (patch: Partial<ModelView>) => void) {
  const { current } = useEnv();
  const enabled = view.examples, i = view.index, lookup = view.find, lookupField = view.ff;
  const setI = (n: number) => change({ index: n });
  const hits = useAsync(() => {
    if (!enabled || !cls) return Promise.resolve(undefined);
    const q = lookup ? `@class=${quote(cls)} ${lookupField ? `${lookupField}="${lookup.replace(/"/g, '')}"` : quote(lookup)}` : `@class=${quote(cls)}`;
    return Api.search(tid, new URLSearchParams({ q, size: lookup ? '10' : '25', from: '2000-01-01T00:00:00Z' }));
  }, [tid, cls, enabled, lookup, lookupField, current?.id]);
  const list = hits.data?.hits ?? [];
  const hit = list[Math.min(i, list.length - 1)];
  const doc = useAsync(() => (hit ? Api.message(tid, hit.vaultId, hit.uuid, hit.day) : Promise.resolve(undefined)), [tid, hit?.vaultId, hit?.uuid]);
  const values = useMemo(() => new Map(doc.data?.fields ?? []), [doc.data]);
  return {
    enabled, hits, list, hit, doc, values, i, setI, lookup, lookupField,
    find: (id: string) => change({ index: 0, find: id.trim() || undefined, ff: undefined }),
  };
}
type Examples = ReturnType<typeof useExamples>;

/** The bar above the field table: switch the example column on, step through documents, look one up by id. */
function ExampleBar({ tid, ex, on, setOn }: { tid: string; ex: Examples; on: boolean; setOn: (v: boolean) => void }) {
  const navigate = useNavigate();
  const { link } = useEnv();
  const [id, setId] = useState(ex.lookup ?? '');
  const { hit, list } = ex;
  const search = hit ? link(`/s?${new URLSearchParams({ q: `@class=${quote(hit.messageClass)}`, range: 'all' })}`) : undefined;
  const facts: [string, string, boolean, ('ok' | 'fail')?][] = hit ? [
    ['Received', dateTime(hit.timestamp), false],
    ['Vault', hit.vaultName, false],
    ...Object.entries(hit.meta).filter(([, v]) => v).map(([k, v]): [string, string, boolean, ('ok' | 'fail')?] => {
      const label = k.replace(/([a-z])([A-Z])/g, '$1 $2');
      const status = k.toLowerCase() === 'status';
      return [label.charAt(0).toUpperCase() + label.slice(1), v, /id$/i.test(k) || /^[0-9a-f-]{20,}$/i.test(v), status ? (/fail|error/i.test(v) ? 'fail' : 'ok') : undefined];
    }),
    ['Document id', hit.uuid, true],
  ] : [];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Button aria-pressed={on} onClick={() => { setId(''); setOn(!on); }}>{on ? 'Hide actual data' : 'Show actual data'}</Button>
        {on && (
          <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); ex.find(id); }}>
            <input className={cx(inputClass, 'w-64')} placeholder="Find a document by id or value" aria-label="Find an actual document by id or value" value={id} onChange={(e) => setId(e.target.value)} />
            <Button type="submit" disabled={!id.trim()}>Find</Button>
            {ex.lookup && <Button variant="ghost" onClick={() => { setId(''); ex.find(''); }}>Show newest</Button>}
          </form>
        )}
      </div>
      {on && <SearchHint q={id} onFix={(next) => { setId(next); ex.find(next); }} />}
      {on && (
        <div className={cx('rounded-card px-4 py-3 flex flex-col gap-3', EXAMPLE_TINT)}>
          {ex.hits.error && <p className="m-0 text-small text-fail">{ex.hits.error}</p>}
          {ex.hits.loading && !ex.hits.data && <Spinner />}
          {ex.hits.data && list.length === 0 && (
            <p className="m-0 text-small text-n8">{ex.lookup ? `No document of this data model matches “${ex.lookup}”${ex.lookupField ? ` in ${ex.lookupField.split('/').pop()}` : ''}.` : 'No documents of this data model in this environment.'}</p>
          )}
          {hit && (
            <>
              <div className="flex items-center gap-2">
                <IconButton label="Previous document" disabled={ex.i <= 0} onClick={() => ex.setI(ex.i - 1)}><BackIcon size={16} /></IconButton>
                <span className="text-small tabular-nums text-n10 font-medium">
                  {ex.lookup ? `Match ${ex.i + 1} of ${list.length}${ex.lookupField ? ` · ${ex.lookupField.split('/').pop()} = ${ex.lookup}` : ''}` : `Document ${ex.i + 1} of ${list.length}`}
                </span>
                <IconButton label="Next document" disabled={ex.i >= list.length - 1} onClick={() => ex.setI(ex.i + 1)}><ArrowIcon size={16} /></IconButton>
                <span className="text-micro text-n7">{!ex.lookup && ex.hits.data!.total > list.length ? `the newest of ${count(ex.hits.data!.total)} documents` : ''}</span>
                <span className="flex-1" />
                <Menu.Root>
                  <Menu.Trigger aria-label="Document actions"
                    className="size-8 rounded-full grid place-items-center bg-transparent text-n8 border-0 cursor-pointer text-body leading-none hover:bg-n3 focus-visible:ring-2 focus-visible:ring-accent">
                    <span aria-hidden="true">⋯</span>
                  </Menu.Trigger>
                  <Menu.Content align="end" className="min-w-72">
                    <Menu.Item onSelect={() => window.open(Api.payloadUrl(tid, hit.vaultId, hit.uuid, hit.day), '_blank', 'noopener')}>Open payload in a new tab</Menu.Item>
                    <Menu.Separator />
                    <Menu.Label>Leaves Settings</Menu.Label>
                    <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => void navigate(link(`/s?${new URLSearchParams({ q: `@uuid=${hit.uuid}`, range: 'all' })}`))}>Find this document in the data search</Menu.Item>
                    <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => search && void navigate(search)}>View this data model in the data search</Menu.Item>
                  </Menu.Content>
                </Menu.Root>
              </div>
              <dl className="m-0 grid gap-x-6 gap-y-2.5 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
                {facts.map(([label, value, mono, tone]) => (
                  <div key={label} className="flex flex-col gap-0.5 min-w-0">
                    <dt className="text-micro uppercase tracking-[.08em] text-n7">{label}</dt>
                    <dd className={cx('m-0 text-small text-n10 truncate', mono && 'font-mono text-micro leading-5', tone === 'fail' && 'text-fail', tone === 'ok' && 'text-n10')} title={value}>
                      {tone && <span className={cx('inline-block size-1.5 rounded-full mr-1.5 align-middle', tone === 'fail' ? 'bg-fail' : 'bg-ok')} aria-hidden="true" />}<Marked text={value} term={ex.lookup} />
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- users and tenants

export function UsersSettings() {
  const { tenant, canManage, me } = useAuth();
  const toast = useToast();
  const tid = tenant?.id ?? '';
  const users = useAsync(() => (canManage ? Api.users(tid) : Promise.resolve([])), [tid, canManage]);
  const [adding, setAdding] = useUrlFlag('new');
  if (!canManage) return <p className="m-0 text-small text-n8">Only owners manage users.</p>;
  const draftKey = `user.${tid}`;
  return (
    <>
      <Title action={<Button variant="primary" aria-expanded={adding} onClick={() => { if (adding) clearForm(draftKey); setAdding(!adding); }}><PlusIcon size={16} />Add user</Button>}>Users of {tenant?.name}</Title>
      <div className="flex flex-col gap-4">
        {adding && (
          <UserForm draftKey={draftKey} roles={['viewer', 'owner']} onClose={() => setAdding(false)} onSave={async (u) => {
            await Api.createUser(tid, { ...u, role: u.role ?? 'viewer' });
            users.reload();
          }} />
        )}
        <UserTable users={users.data ?? []} loading={users.loading && !users.data} error={users.error} self={me?.user.id} onRemove={async (u) => {
          try {
            await Api.deleteUser(tid, u.id);
            toast(`${u.email} removed`);
            users.reload();
          } catch (e) {
            toast(message(e), 'fail');
          }
        }} />
      </div>
    </>
  );
}

export function TenantsSettings() {
  const { isAdmin, reload, setTenantId, me } = useAuth();
  const toast = useToast();
  const tenants = useAsync(() => (isAdmin ? Api.tenants() : Promise.resolve([])), [isAdmin]);
  const admins = useAsync(() => (isAdmin ? Api.admins() : Promise.resolve([])), [isAdmin]);
  const [creating, setCreating] = useUrlFlag('new');
  const [addingAdmin, setAddingAdmin] = useUrlFlag('admin');
  // The typed name survives leaving and Back (sessionStorage); cleared by a create or a cancel
  const [{ name }, setDraft, clearDraft] = useFormDraft('tenant', { name: '' });
  const setName = (n: string) => setDraft({ name: n });
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return <p className="m-0 text-small text-n8">Only administrators manage tenants.</p>;

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const t = await Api.createTenant(name.trim());
      clearDraft();
      setCreating(false);
      tenants.reload();
      await reload();
      setTenantId(t.id);
      toast(`Tenant ${t.name} created and selected. Add an owner under Users.`);
    } catch (err) {
      toast(message(err), 'fail');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Title action={<Button variant="primary" aria-expanded={creating} onClick={() => { if (creating) clearDraft(); setCreating(!creating); }}><PlusIcon size={16} />New tenant</Button>}>Tenants</Title>
      <div className="flex flex-col gap-4">
        {creating && (
          <Panel title="New tenant" onClose={() => { clearDraft(); setCreating(false); }}>
            <form onSubmit={(e) => void create(e)} className="flex items-end gap-3 flex-wrap">
              <Field label="Name" className="w-72"><input className={inputClass} placeholder="Customer Oy" autoFocus value={name} onChange={(e) => setName(e.target.value)} /></Field>
              <Button type="submit" variant="primary" disabled={busy || !name.trim()}>Create</Button>
            </form>
          </Panel>
        )}
        {tenants.error && <p className="m-0 text-small text-fail">{tenants.error}</p>}
        <Surface className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead><tr><th className={th}>Name</th><th className={th}>Id</th><th className={th}>Created</th></tr></thead>
            <tbody>
              {tenants.data?.map((t) => (
                <tr key={t.id} className={row} onClick={() => { setTenantId(t.id); toast(`Switched to ${t.name}`); }}>
                  <td className={cx(td, 'font-medium text-n10')}>{t.name}</td>
                  <td className={cx(td, 'font-mono text-micro text-n7')}>{t.id}</td>
                  <td className={cx(td, 'text-n9 whitespace-nowrap')}>{dateTime(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {tenants.loading && !tenants.data && <div className="p-6 grid place-items-center"><Spinner /></div>}
          {tenants.data && tenants.data.length === 0 && <div className="p-6 text-small text-n7 text-center">No tenants yet.</div>}
        </Surface>
      </div>

      <div className="mt-10">
        <Title action={<Button aria-expanded={addingAdmin} onClick={() => { if (addingAdmin) clearForm('admin.new'); setAddingAdmin(!addingAdmin); }}><PlusIcon size={16} />Add administrator</Button>}>Administrators</Title>
      </div>
      <div className="flex flex-col gap-4">
        {addingAdmin && (
          <UserForm draftKey="admin.new" roles={[]} onClose={() => setAddingAdmin(false)} onSave={async (u) => {
            await Api.createAdmin({ email: u.email, name: u.name, password: u.password });
            admins.reload();
          }} />
        )}
        <UserTable users={admins.data ?? []} loading={admins.loading && !admins.data} error={admins.error} self={me?.user.id} onRemove={async (u) => {
          try {
            await Api.deleteAdmin(u.id);
            toast(`${u.email} removed`);
            admins.reload();
          } catch (e) {
            toast(message(e), 'fail');
          }
        }} />
      </div>
    </>
  );
}

function UserTable({ users, self, loading, error, onRemove }: { users: User[]; self?: string; loading?: boolean; error?: string; onRemove: (u: User) => void }) {
  const [removing, setRemoving] = useState<User>();
  return (
    <>
      {removing && (
        <Confirm question={`Remove ${removing.email}?`} action="Remove" onCancel={() => setRemoving(undefined)}
          onConfirm={() => { onRemove(removing); setRemoving(undefined); }}>
          The user is logged out and can no longer sign in.
        </Confirm>
      )}
      {error && <p className="m-0 text-small text-fail">{error}</p>}
      <Surface className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead><tr><th className={th}>Name</th><th className={th}>Email</th><th className={th}>Role</th><th className={th}>Last login</th><th className={th} /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className={cx(td, 'font-medium text-n10')}>{u.name}</td>
                <td className={cx(td, 'text-n9')}>{u.email}</td>
                <td className={td}>
                  <span className={cx('inline-flex items-center h-6 px-2.5 rounded-full text-micro font-medium', u.role === 'viewer' ? 'bg-n3 text-n9' : 'bg-accent-soft text-accent')}>{u.role}</span>
                </td>
                <td className={cx(td, 'text-n7 whitespace-nowrap')}>{ago(u.lastLoginAt)}</td>
                <td className={cx(td, 'text-right py-2')}>
                  {u.id !== self && <IconButton label={`Remove ${u.email}`} onClick={() => setRemoving(u)}><TrashIcon size={16} /></IconButton>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-6 grid place-items-center"><Spinner /></div>}
        {!loading && users.length === 0 && <div className="p-6 text-small text-n7 text-center">No users yet.</div>}
      </Surface>
    </>
  );
}

/** Adding a user (with a role) or an administrator (no role), on the page. */
function UserForm({ draftKey, roles, onClose, onSave }: {
  draftKey: string; roles: Role[]; onClose: () => void;
  onSave: (u: { email: string; name: string; role?: Role; password: string }) => Promise<void>;
}) {
  const toast = useToast();
  const admin = roles.length === 0;
  // Email, name and role are kept as a draft (sessionStorage) so Back returns to the filled form; the password never is
  const [d, setDraft, clearDraft] = useFormDraft(draftKey, { email: '', name: '', role: (roles[0] ?? 'admin') as string });
  const { email, name } = d;
  const role: Role = roles.includes(d.role as Role) ? (d.role as Role) : roles[0] ?? 'admin';
  const setEmail = (v: string) => setDraft({ email: v });
  const setName = (v: string) => setDraft({ name: v });
  const setRole = (v: Role) => setDraft({ role: v });
  const close = () => { clearDraft(); onClose(); };
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSave({ email, name, role, password });
      toast(`${admin ? 'Administrator' : 'User'} ${email} added. Give them the initial password securely.`);
      close();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title={admin ? 'Add administrator' : 'Add user'} onClose={close}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email"><input className={inputClass} type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label="Name"><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          {!admin && (
            <Field label="Role" hint={role === 'owner' ? 'Searches, installs and revokes vaults, manages users' : 'Searches and reads documents'}>
              <select className={inputClass} value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
          <Field label="Initial password" hint="At least 10 characters; the user can change it" error={error}>
            <input className={inputClass} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !email || password.length < 10}>Add</Button>
        </div>
      </form>
    </Panel>
  );
}
